import { Link } from "@inertiajs/react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import type { LucideIcon } from "lucide-react"
import type { ReactNode } from "react"

/* ── Header Action Button ─────────────────────────────────── */

interface HeaderAction {
  label: string
  icon: LucideIcon
  onClick?: () => void
  href?: string
}

/* ── Column Definition ────────────────────────────────────── */

export interface HpiColumn<T> {
  /** Column header label (top line) */
  header: string
  /** Optional second header line */
  subHeader?: string
  /** Render cell content */
  render: (row: T, index: number) => ReactNode
  /** Column width class (e.g. "w-[120px]") */
  width?: string
  /** Text alignment */
  align?: "left" | "center" | "right"
  /** Hide this column on mobile screens (<768px) */
  hideOnMobile?: boolean
  /** Extra class(es) applied to each cell in this column */
  cellClassName?: string
}

/* ── Pagination ───────────────────────────────────────────── */

interface PaginationProps {
  currentPage: number
  totalPages: number
  totalItems: number
  perPage: number
  onPageChange: (page: number) => void
}

export function HpiPagination({
  currentPage,
  totalPages,
  totalItems,
  perPage,
  onPageChange,
}: PaginationProps) {
  const start = (currentPage - 1) * perPage + 1
  const end = Math.min(currentPage * perPage, totalItems)

  return (
    <div className="text-hpi-dark-gray flex items-center justify-center gap-3 py-4 text-sm">
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage <= 1}
        className="text-hpi-active-blue disabled:text-hpi-med-gray"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <span className="font-medium tracking-wide">
        {start}-{end} OF {totalItems}
      </span>
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage >= totalPages}
        className="text-hpi-active-blue disabled:text-hpi-med-gray"
      >
        <ChevronRight className="h-5 w-5" />
      </button>
    </div>
  )
}

/* ── Simple static pagination (no callback, display only) ── */

export function HpiPaginationStatic({
  start,
  end,
  total,
}: {
  start: number
  end: number
  total: number
}) {
  return (
    <div className="text-hpi-dark-gray flex items-center justify-center gap-3 py-4 text-sm">
      <button className="text-hpi-active-blue">
        <ChevronLeft className="h-5 w-5" />
      </button>
      <span className="font-medium tracking-wide">
        {start}-{end} OF {total}
      </span>
      <button className="text-hpi-active-blue">
        <ChevronRight className="h-5 w-5" />
      </button>
    </div>
  )
}

/* ── Main Data Table ──────────────────────────────────────── */

interface HpiDataTableProps<T> {
  /** Title shown in blue header bar (rendered uppercase) */
  title: string
  /** Action buttons in header bar */
  actions?: HeaderAction[]
  /** Column definitions */
  columns: HpiColumn<T>[]
  /** Data rows */
  data: T[]
  /** Unique key extractor */
  keyFn: (row: T) => string | number
  /** Show selection controls */
  selectable?: boolean
  /** Currently selected row key (single-select mode) */
  selectedKey?: string | number | null
  /** Currently selected row keys (multi-select mode) */
  selectedKeys?: Set<string | number>
  /** Selection callback */
  onSelect?: (key: string | number) => void
  /** Pagination (renders below table if provided) */
  pagination?: ReactNode
  /** Make each row a link — return the href for a given row (return undefined to skip linking) */
  rowHref?: (row: T) => string | undefined
  /** Link target (e.g. "_blank" to open in new tab). Uses Inertia Link when omitted, plain <a> when set. */
  rowTarget?: string
  /** Message shown when data is empty */
  emptyMessage?: string
  /** Additional className on the outer wrapper */
  className?: string
}

export function HpiDataTable<T>({
  title,
  actions,
  columns,
  data,
  keyFn,
  selectable = false,
  selectedKey,
  selectedKeys,
  onSelect,
  rowHref,
  rowTarget,
  pagination,
  emptyMessage,
  className = "",
}: HpiDataTableProps<T>) {
  return (
    <div className={className}>
      {/* Blue header bar */}
      <div className="bg-hpi-dark-blue flex items-center justify-between rounded-t px-4 py-2.5">
        <h3 className="text-sm font-semibold tracking-wider text-white uppercase">
          {title}
        </h3>
        {actions && actions.length > 0 && (
          <div className="flex items-center gap-4">
            {actions.map((action) => {
              const Icon = action.icon
              return action.href ? (
                <Link
                  key={action.label}
                  href={action.href}
                  className="flex items-center gap-1.5 text-xs text-white/80 hover:text-white"
                >
                  <Icon className="h-4 w-4" />
                  <span>{action.label}</span>
                </Link>
              ) : (
                <button
                  key={action.label}
                  onClick={action.onClick}
                  className="flex cursor-pointer items-center gap-1.5 text-xs text-white/80 hover:text-white"
                >
                  <Icon className="h-4 w-4" />
                  <span>{action.label}</span>
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* Column headers */}
      <div className="border-hpi-light-gray border-b bg-white px-4 py-2">
        <div className="flex items-start gap-3">
          {selectable && <div className="w-8 shrink-0" />}
          {columns.map((col, i) => (
            <div
              key={i}
              className={`min-w-0 flex-1 ${col.width ?? ""} ${col.align === "right" ? "text-right" : col.align === "center" ? "text-center" : "text-left"} ${col.hideOnMobile ? "hidden md:block" : ""}`}
            >
              <div className="text-hpi-active-blue text-xs font-medium">
                {col.header}
              </div>
              {col.subHeader && (
                <div className="text-hpi-active-blue text-xs font-medium">
                  {col.subHeader}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Data rows */}
      <div className="bg-white">
        {data.length === 0 ? (
          <div className="text-hpi-med-gray px-4 py-8 text-center text-sm">
            {emptyMessage ?? "No records found."}
          </div>
        ) : (
          data.map((row, rowIndex) => {
            const key = keyFn(row)
            const href = rowHref?.(row)
            const rowClasses = `flex items-start gap-3 border-b border-hpi-light-gray/50 px-4 py-3 last:border-b-0${href ? " hover:bg-hpi-pale-gray transition-colors" : ""}`
            const cells = (
              <>
                {selectable && (
                  <div className="w-8 shrink-0 pt-0.5">
                    <button
                      type="button"
                      onClick={(e) => {
                        if (href) {
                          e.preventDefault()
                          e.stopPropagation()
                        }
                        onSelect?.(key)
                      }}
                      className={`h-4 w-4 border-2 ${
                        selectedKeys
                          ? `rounded-sm ${selectedKeys.has(key) ? "border-hpi-active-blue bg-hpi-active-blue" : "border-hpi-med-gray"}`
                          : `rounded-full ${selectedKey === key ? "border-hpi-active-blue bg-hpi-active-blue" : "border-hpi-med-gray"}`
                      }`}
                    />
                  </div>
                )}
                {columns.map((col, colIndex) => (
                  <div
                    key={colIndex}
                    className={`text-hpi-dark-gray min-w-0 flex-1 overflow-hidden text-sm ${col.width ?? ""} ${col.align === "right" ? "text-right" : col.align === "center" ? "text-center" : "text-left"} ${col.hideOnMobile ? "hidden md:block" : ""} ${col.cellClassName ?? ""}`}
                  >
                    {col.render(row, rowIndex)}
                  </div>
                ))}
              </>
            )
            return href ? (
              rowTarget ? (
                <a
                  key={key}
                  href={href}
                  target={rowTarget}
                  rel="noopener noreferrer"
                  className={rowClasses}
                >
                  {cells}
                </a>
              ) : (
                <Link key={key} href={href} className={rowClasses}>
                  {cells}
                </Link>
              )
            ) : (
              <div key={key} className={rowClasses}>
                {cells}
              </div>
            )
          })
        )}
      </div>

      {/* Pagination */}
      {pagination}
    </div>
  )
}
